import { useEffect, useMemo, useState } from "react";
import { MessageBarBody, MessageBarTitle, Spinner } from "@fluentui/react-components";
import moment from "moment";
import { useTranslation } from "react-i18next";

import { DebtPaymentImputation } from "../../../../models/Entities/CashFlow/Payment/DebtPaymentImputation";
import { formatCurrencyAmountWithIssue } from "../../../../commons/currency/Currency.Functions";
import { candidateDebts, imputationTotals, inPaymentCurrency } from "../../CashFlow.Form.Logic";
import { PartyChip, SaveErrorBanner, Step } from "../../CashFlow.Form.Bits";
import { GuideDrawer, GuideDrawerSection } from "../../../../theme/components/GuideDrawer";
import { GuideMessageBar } from "../../../../theme/components/GuideMessageBar";
import { MEDICAL_CENTER_NAME } from "../../General/Cashflow.General.Functions";
import { ImputationPicker } from "../Payment.Form/Payment.Imputation.Picker";
import * as ComponentFunctions from './PaymentImputation.Form.Function';
import "../../CashFlow.Form.Styles.css";

const fmt = (amount: number | undefined | null, currency: string | undefined | null) =>
    formatCurrencyAmountWithIssue(amount ?? 0, currency || 'ARS');

/**
 * Aplicar lo que un cobro (o pago) todavía tiene sin imputar a las deudas de
 * la misma contraparte. Mismo selector que al registrar el movimiento.
 */
const PaymentImputationFormComponent = (props: ComponentFunctions.Props) => {
    const [t] = useTranslation();
    const [imputations, setImputations] = useState<DebtPaymentImputation[]>([]);
    const [submitted, setSubmitted] = useState<boolean>(false);
    const [remaining, setRemaining] = useState<number>(0);
    const [saveError, setSaveError] = useState<string | undefined>(undefined);

    const payment = props.payment;
    const currency = payment?.currencyCode || 'ARS';
    const pending = Math.round((payment?.pendingIndictmentAmount ?? 0) * 100) / 100;
    const counterpartId = payment ? (props.CenterCollects ? payment.payer : payment.receiver) : undefined;
    const counterpartName = props.NameOf(counterpartId);

    // Al abrir: deudas y nombres, si todavía no están.
    useEffect(() => {
        if (!props.isOpen || !payment) return;
        setImputations([]);
        setSubmitted(false);
        setSaveError(undefined);
        props.GetAllPersons();
        if (!props.debtsLoaded && !props.isLoadingDebts) {
            if (props.CenterCollects) props.GetAllDebtsByCreditor(props.OwnerId, undefined);
            else props.GetAllDebtsByDebtor(props.OwnerId, undefined);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.isOpen, payment?.id]);

    // Se aplica una deuda por pedido; cuando terminan todos, se cierra o se avisa.
    useEffect(() => {
        if (!submitted || remaining > 0 || props.isCreatingImputation) return;
        if (props.failOnCreatingImputation) {
            setSaveError(props.error?.ErrorMessage || t('cashflow.imputations.createError', 'No se pudo guardar la imputación.'));
            setSubmitted(false);
            return;
        }
        if (payment) props.GetPaymentImputations(payment.id);
        setSubmitted(false);
        props.onClose();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [submitted, remaining, props.isCreatingImputation, props.failOnCreatingImputation]);

    const candidates = useMemo(
        () => candidateDebts(props.CandidateDebts, undefined, () => ''),
        [props.CandidateDebts]
    );

    const totals = imputationTotals(imputations, pending);
    const canApply = imputations.length > 0 && !totals.over && imputations.every((item) => item.exchangeRate > 0) && !props.isCreatingImputation && !submitted;

    const apply = async () => {
        if (!payment || !canApply) return;
        setSaveError(undefined);
        setSubmitted(true);
        setRemaining(1);
        // Todas las deudas en un solo pedido. El picker trabaja en moneda de la deuda; la API espera el importe en moneda del pago.
        const debtsPaymentImputation = imputations.map((item) => ({
            debtId: item.debtId,
            amount: Math.round(inPaymentCurrency(item) * 100) / 100,
            exchangeRate: item.exchangeRate
        }));
        const total = Math.round(debtsPaymentImputation.reduce((acc, item) => acc + item.amount, 0) * 100) / 100;
        await props.CreatePaymentImputation({ paymentId: payment.id, id: 0, amount: total, currencyCode: currency, debtsPaymentImputation });
        setRemaining(0);
    };

    const close = () => { setImputations([]); setSubmitted(false); props.onClose(); };

    if (!payment) return null;

    const kind = props.CenterCollects ? 'cobro' : 'pago';
    const imputed = payment.imputedAmount ?? 0;
    const imputedPct = payment.amount > 0 ? Math.min(100, (imputed / payment.amount) * 100) : 0;

    const summary = imputations.length > 0
        ? <>
            Se aplican <b className="cff-num">{fmt(totals.imputed, currency)}</b> a {imputations.length} {imputations.length === 1 ? 'deuda' : 'deudas'}
            {totals.remaining > 0.005 && <> · quedan {fmt(totals.remaining, currency)} sin imputar</>}
        </>
        : undefined;

    return (
        <GuideDrawer
            open={props.isOpen}
            size="large"
            title={props.title ?? t('cashflow.imputations.formTitle', 'Imputar saldo pendiente')}
            eyebrow={`Flujo de caja · ${props.CenterCollects ? 'Cobro' : 'Pago'} #${payment.id}`}
            subtitle={summary}
            onDismiss={close}
            closeLabel={t('common.close', 'Cerrar')}
            testId="payment-imputation-drawer"
            secondaryAction={{ label: t('cashflow.imputations.cancel', 'Cancelar'), disabled: submitted }}
            primaryAction={{ label: 'Aplicar a las deudas', onClick: apply, disabled: !canApply, loading: submitted }}
        >
            {saveError && <SaveErrorBanner title="No se pudo aplicar." message={saveError} />}

            {/* Qué se está imputando */}
            <GuideDrawerSection className="cff-root">
                <div className="cff-parties">
                    <PartyChip role={props.CenterCollects ? 'Pagó' : 'Cobró'} name={counterpartName} />
                    <span className="cff-parties__arrow" aria-hidden>→</span>
                    <PartyChip role={props.CenterCollects ? 'Cobró' : 'Pagó'} name={MEDICAL_CENTER_NAME} />
                </div>
                <div className="cff-summary">
                    <div className="cff-summary__item">
                        <div className="cff-summary__label">{props.CenterCollects ? 'Cobro' : 'Pago'} del {payment.paymentDate ? moment(payment.paymentDate).format('DD/MM/YYYY') : '—'}</div>
                        <div className="cff-summary__value cff-num">{fmt(payment.amount, currency)}</div>
                    </div>
                    <div className="cff-summary__item">
                        <div className="cff-summary__label">Ya imputado · {imputedPct.toFixed(0)}%</div>
                        <div className="cff-summary__value cff-num">{fmt(imputed, currency)}</div>
                    </div>
                    <div className="cff-summary__item">
                        <div className="cff-summary__label">Sin imputar</div>
                        <div className="cff-summary__value cff-summary__value--pending cff-num">{fmt(pending, currency)}</div>
                    </div>
                </div>
            </GuideDrawerSection>

            {/* A qué deudas */}
            {pending > 0 ? (
                <div className="cff-root">
                    <Step title="¿A qué deudas se aplica?"
                        hint={<>Pendientes de {counterpartName}, más viejas primero. Sólo se puede aplicar hasta {fmt(pending, currency)}.</>}>
                        {props.isLoadingDebts && candidates.length === 0
                            ? <div className="cff-state"><Spinner size="extra-tiny" /> Buscando deudas…</div>
                            : (
                                <ImputationPicker
                                    debts={candidates}
                                    paymentAmount={pending}
                                    paymentCurrency={currency}
                                    counterpartName={counterpartName}
                                    value={imputations}
                                    onChange={setImputations}
                                />
                            )}
                    </Step>
                </div>
            ) : (
                <GuideMessageBar intent="warning" layout="multiline">
                    <MessageBarBody>
                        <MessageBarTitle>Este {kind} ya está imputado por completo.</MessageBarTitle>
                        No queda saldo para aplicar a otras deudas.
                    </MessageBarBody>
                </GuideMessageBar>
            )}
        </GuideDrawer>
    );
};

export default ComponentFunctions.connector(PaymentImputationFormComponent);
